import { useEffect, useState } from 'react'
import { listDashboards } from '~/api/monitoring'
import type { AdminDashboard } from '~/api/types'
import { Button } from '~/components/ui/button'
import {
    DataListTable,
    type DataListColumn,
} from '~/components/ui/data-list-table'
import { SearchInput } from '~/components/ui/search-input'
import { formatDateTime, whoLabel } from '~/lib/utils'

// 모니터링 대시보드 목록 테이블. limit/offset 페이지네이션 + 행 클릭 콜백.
const PAGE_SIZE = 20

const COLUMNS: DataListColumn<AdminDashboard>[] = [
    {
        key: 'name',
        header: '이름',
        className: 'font-bold',
        render: (d) => d.name,
    },
    {
        key: 'owner',
        header: '사용자',
        className: 'max-w-[180px] truncate',
        title: (d) => d.ownerEmail ?? undefined,
        render: (d) => whoLabel(d.ownerName, d.ownerEmail),
    },
    {
        key: 'assessmentId',
        header: '평가 ID',
        className: 'font-mono text-xs',
        render: (d) => (d.assessmentId ? d.assessmentId.slice(0, 8) : '-'),
    },
    {
        key: 'notifyEmail',
        header: '알림 이메일',
        render: (d) => d.notifyEmail ?? '-',
    },
    {
        key: 'createdAt',
        header: '생성일',
        render: (d) => formatDateTime(d.createdAt),
    },
]

export function DashboardTable({
    onSelect,
    selectedId,
}: {
    onSelect?: (dashboard: AdminDashboard) => void
    selectedId?: string
}) {
    const [items, setItems] = useState<AdminDashboard[]>([])
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [q, setQ] = useState('')
    const [offset, setOffset] = useState(0)

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        listDashboards({ q: q.trim() || undefined, limit: PAGE_SIZE, offset })
            .then((data) => {
                if (alive) setItems(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [q, offset])

    return (
        <div>
            <div className='mb-4'>
                <SearchInput
                    className='w-72'
                    value={q}
                    onChange={(v) => {
                        setOffset(0)
                        setQ(v)
                    }}
                    placeholder='대시보드·사용자 검색'
                />
            </div>

            <DataListTable
                items={items}
                loading={loading}
                error={error}
                columns={COLUMNS}
                getRowId={(d) => d.id}
                onRowClick={onSelect}
                selectedId={selectedId}
                empty='대시보드가 없어요.'
            />

            <div className='mt-4 flex items-center justify-between'>
                <span className='text-muted-foreground text-xs'>
                    {offset + 1}–{offset + items.length}
                </span>
                <div className='flex gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={offset === 0}
                        onClick={() =>
                            setOffset((o) => Math.max(0, o - PAGE_SIZE))
                        }>
                        이전
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={items.length < PAGE_SIZE}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                        다음
                    </Button>
                </div>
            </div>
        </div>
    )
}
